import React, { useEffect } from "react";
import { Table } from "antd";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const currency = localStorage.getItem("currencycode") || "₹";

const DateRangeSummary = ({ rooms, applicableTaxes, dateRange, setExportData, setExportHeaders, setExportFooter, setTotalsForPageFooter, setTotalColumns, setReportTitle }) => {
  
  console.log("Received rooms data:", rooms); // Debug log for input data
  const { t } = useTranslation();
  const groupedData = () => {
    const grouped = {};
    rooms.forEach((room) => {
      const date = moment(room.check_in).format("YYYY-MM-DD");
      if (!grouped[date]) {
        // Use API values directly for the first (and likely only) entry per date
        grouped[date] = {
          key: date,
          date: moment(date).format("DD MMM YYYY"),
          room_sold_count: room.room_sold_count || 0,
          actual_price: room.rate_per_night * room.nights || 0,
          discount: room.discount || 0,
          net_revenue: room.total_revenue || 0,
          ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: room[taxName] || 0 }), {}),
        };
      } else {
        // Aggregate only if multiple summarized entries exist for the same date (unlikely)
        grouped[date].room_sold_count += room.room_sold_count || 0;
        grouped[date].actual_price  += room.rate_per_night * room.nights || 0;
        grouped[date].discount += room.discount || 0;
        grouped[date].net_revenue += room.total_revenue || 0;
        applicableTaxes.forEach((taxName) => {
          grouped[date][taxName] += room[taxName] || 0;
        });
      }
    });
    console.log("Grouped data:", Object.values(grouped)); // Debug log for grouped data
    return Object.values(grouped);
  };

  const sortedData = () => {
    return [...groupedData()].sort((a, b) => moment(a.key).unix() - moment(b.key).unix());
  };

  const totals = groupedData().reduce(
    (acc, item) => ({
      room_sold_count: acc.room_sold_count + (item.room_sold_count || 0),
      actual_price: acc.actual_price + (item.actual_price || 0),
      discount: acc.discount + (item.discount || 0),
      net_revenue: acc.net_revenue + (item.net_revenue || 0),
      ...applicableTaxes.reduce((taxAcc, taxName) => ({
        ...taxAcc,
        [taxName]: (taxAcc[taxName] || 0) + (item[taxName] || 0),
      }), acc),
    }),
    {
      room_sold_count: 0,
      actual_price: 0,
      discount: 0,
      net_revenue: 0,
      ...applicableTaxes.reduce((acc, taxName) => ({ ...acc, [taxName]: 0 }), {}),
    }
  );

  console.log("Totals calculated:", totals); // Debug log for totals

  const barChartData = {
    labels: sortedData().map((item) => item.date),
    datasets: [
      {
        label: t("rooms_sold_count"),
        data: sortedData().map((item) => item.room_sold_count),
        backgroundColor: "#28a745",
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { beginAtZero: true, title: { display: true, text: t("rooms_sold_count") } },
      x: { title: { display: true, text: t("date") } },
    },
    plugins: {
      legend: { position: "top" },
    },
  };

  const exportHeaders = [
    { key: "date", label: t("date") },
    { key: "room_sold_count", label: t("room_sold_count") },
    { key: "actual_price", label: `${t("actual_price")} (${currency})` },
    { key: "discount", label: `${t("commission_discount")} (${currency})` },
    ...applicableTaxes.map((taxName) => ({
      key: taxName,
      label: `${taxName} (${currency})`,
    })),
    { key: "net_revenue", label: `${t("net_revenue")} (${currency})` },
  ];

  const exportData = groupedData().map((item) => ({
    date: item.date,
    room_sold_count: item.room_sold_count,
    actual_price: item.actual_price.toFixed(2),
    discount: item.discount.toFixed(2),
    ...applicableTaxes.reduce((acc, taxName) => ({
      ...acc,
      [taxName]: item[taxName].toFixed(2),
    }), {}),
    net_revenue: item.net_revenue.toFixed(2),
  }));

  const totalsForPageFooter = [
    { key: t("total_bookings"), value: totals.room_sold_count },
  ];

  const exportFooter = [
    { key: "room_sold_count", value: totals.room_sold_count, currency: false },
    { key: "actual_price", value: totals.actual_price, currency: true },
    { key: "discount", value: totals.discount, currency: true },
    ...applicableTaxes.map((taxName) => ({
      key: taxName,
      value: totals[taxName],
      currency: true,
    })),
    { key: "net_revenue", value: totals.net_revenue, currency: true },
  ];

  useEffect(() => {
    setExportData(exportData);
    setExportHeaders(exportHeaders);
    setExportFooter(exportFooter);
    setTotalsForPageFooter(totalsForPageFooter);
    setTotalColumns(["room_sold_count", "actual_price", "discount", ...applicableTaxes, "net_revenue"]);
    setReportTitle(t("date_range_summary"));
  }, [rooms, applicableTaxes, setExportData, setExportHeaders, setExportFooter, setTotalsForPageFooter, setTotalColumns, setReportTitle, t]);

const columns = [
  {
    title: t("date"),
    dataIndex: "date",
    key: "date",
    render: (text) => moment(text).format("DD MMM YYYY"),
    sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
  },
  {
    title: t("room_sold_count"),
    dataIndex: "room_sold_count",
    key: "room_sold_count",
    sorter: (a, b) => a.room_sold_count - b.room_sold_count,
  },
  {
    title: t("actual_price"),
    dataIndex: "actual_price",
    key: "actual_price",
    render: (text) => `${currency}${text.toFixed(2)}`,
    sorter: (a, b) => a.actual_price - b.actual_price,
  },
  {
    title: t("commission_discount"),
    dataIndex: "discount",
    key: "discount",
    render: (text) => `${currency}${text.toFixed(2)}`,
    sorter: (a, b) => a.discount - b.discount,
  },
  ...applicableTaxes.map((taxName) => ({
    title: `${taxName} (${currency})`,
    dataIndex: taxName,
    key: taxName,
    render: (text) => `${currency}${(text || 0).toFixed(2)}`,
    sorter: (a, b) => (a[taxName] || 0) - (b[taxName] || 0),
  })),
  {
    title: t("net_revenue"),
    dataIndex: "net_revenue",
    key: "net_revenue",
    render: (text) => `${currency}${text.toFixed(2)}`,
    sorter: (a, b) => a.net_revenue - b.net_revenue,
    onCell: () => ({ style: { backgroundColor: "#e2e6ea" } }),
  },
];

  return (
    <div className="date-range-summary-container">
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
            <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
              <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {t("date_wise_room_sold_count")}
              </h5>
              <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#28a745" }} />
            </div>
            <div className="card-body" style={{ padding: "10px", height: "300px" }}>
              <div style={{ height: "100%", width: "100%" }}>
                <Bar data={barChartData} options={barChartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row g-0">
        <div className="col-md-12">
          <div className="card" style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}>
            <div className="card-header" style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}>
              <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {t("date_range_summary")} (Total Bookings: {totals.room_sold_count})
              </h5>
              <div style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#007bff" }} />
            </div>
            <div className="card-body p-0">
              {rooms.length === 0 ? (
                <div className="no-data-container text-center mt-4" style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                  <img
                    src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                    alt="No data available"
                    style={{ width: "150px", marginBottom: "20px" }}
                  />
                  <h3>{t("no_data")}</h3>
                  <p>{t("no_room_sold_data")}</p>
                </div>
              ) : (
                <Table
                  dataSource={sortedData()}
                  columns={columns}
                  rowKey="key"
                  pagination={false}
                  bordered
                  className="daybook-table"
                  style={{ margin: 0 }}
                  summary={() => (
                    <Table.Summary.Row style={{ backgroundColor: "#e6f4ea", fontWeight: "700", fontSize: "15px" }}>
                      <Table.Summary.Cell>{t("total")}</Table.Summary.Cell>
                      <Table.Summary.Cell>{totals.room_sold_count}</Table.Summary.Cell>
                      <Table.Summary.Cell>{currency}{totals.actual_price.toFixed(2)}</Table.Summary.Cell>
                      <Table.Summary.Cell>{currency}{totals.discount.toFixed(2)}</Table.Summary.Cell>
                      {applicableTaxes.map((taxName, index) => (
                        <Table.Summary.Cell key={index}>{currency}{totals[taxName].toFixed(2)}</Table.Summary.Cell>
                      ))}
                      <Table.Summary.Cell style={{ backgroundColor: "#e2e6ea" }}>{currency}{totals.net_revenue.toFixed(2)}</Table.Summary.Cell>
                    </Table.Summary.Row>
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .table th, .table td {
          padding: 8px 12px;
          vertical-align: middle;
          border: 1px solid #ced4da;
        }
        .daybook-table .ant-table-thead > tr > th {
          background: #ffffff;
          font-weight: 600;
          padding: 8px 16px;
          font-size: 14px;
        }
        .daybook-table .ant-table-tbody > tr > td {
          padding: 12px 18px;
          font-size: 14px;
          height: 60px;
        }
        .daybook-table .ant-table {
          border: 1px solid #dee2e6;
        }
        .daybook-table .ant-table-tbody > tr.ant-table-row:hover > td {
          background: #f5f5f5;
        }
        .chart-container {
          max-height: 300px;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default DateRangeSummary;